"use client";

import { c as _c } from "react/compiler-runtime";
import { Loader2, Locate, Maximize, Minus, Plus } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "#/lib/utils";
import { useMap } from "./map";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const positionClasses = {
  "top-left": "top-2 left-2",
  "top-right": "top-2 right-2",
  "bottom-left": "bottom-2 left-2",
  "bottom-right": "bottom-10 right-2"
};
function ControlGroup(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = /*#__PURE__*/_jsx("div", {
      className: "border-border bg-background [&>button:not(:last-child)]:border-border flex flex-col overflow-hidden rounded-md border shadow-sm [&>button:not(:last-child)]:border-b",
      children: children
    });
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function ControlButton(t0) {
  const $ = _c(8);
  const {
    onClick,
    label,
    children,
    disabled: t1
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const t2 = disabled && "pointer-events-none cursor-not-allowed opacity-50";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("hover:bg-accent dark:hover:bg-accent/40 flex size-8 items-center justify-center transition-colors", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== children || $[3] !== disabled || $[4] !== label || $[5] !== onClick || $[6] !== t3) {
    t4 = /*#__PURE__*/_jsx("button", {
      onClick: onClick,
      "aria-label": label,
      type: "button",
      className: t3,
      disabled: disabled,
      children: children
    });
    $[2] = children;
    $[3] = disabled;
    $[4] = label;
    $[5] = onClick;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function MapControls(t0) {
  const $ = _c(34);
  const {
    position: t1,
    showZoom: t2,
    showCompass: t3,
    showLocate: t4,
    showFullscreen: t5,
    className,
    onLocate
  } = t0;
  const position = t1 === undefined ? "bottom-right" : t1;
  const showZoom = t2 === undefined ? true : t2;
  const showCompass = t3 === undefined ? false : t3;
  const showLocate = t4 === undefined ? false : t4;
  const showFullscreen = t5 === undefined ? false : t5;
  const {
    map
  } = useMap();
  const [waitingForLocation, setWaitingForLocation] = useState(false);
  let t6;
  if ($[0] !== map) {
    t6 = () => {
      map?.zoomTo(map.getZoom() + 1, {
        duration: 300
      });
    };
    $[0] = map;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const handleZoomIn = t6;
  let t7;
  if ($[2] !== map) {
    t7 = () => {
      map?.zoomTo(map.getZoom() - 1, {
        duration: 300
      });
    };
    $[2] = map;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const handleZoomOut = t7;
  let t8;
  if ($[4] !== map) {
    t8 = () => {
      map?.resetNorthPitch({
        duration: 300
      });
    };
    $[4] = map;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const handleResetBearing = t8;
  let t9;
  if ($[6] !== map || $[7] !== onLocate) {
    t9 = () => {
      setWaitingForLocation(true);
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(pos => {
          const coords = {
            longitude: pos.coords.longitude,
            latitude: pos.coords.latitude
          };
          map?.flyTo({
            center: [coords.longitude, coords.latitude],
            zoom: 14,
            duration: 1500
          });
          onLocate?.(coords);
          setWaitingForLocation(false);
        }, error => {
          console.error("Error getting location:", error);
          setWaitingForLocation(false);
        });
      }
    };
    $[6] = map;
    $[7] = onLocate;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const handleLocate = t9;
  let t10;
  if ($[9] !== map) {
    t10 = () => {
      const container = map?.getContainer();
      if (!container) {
        return;
      }
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        container.requestFullscreen();
      }
    };
    $[9] = map;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const handleFullscreen = t10;
  const t11 = positionClasses[position];
  let t12;
  if ($[11] !== className || $[12] !== t11) {
    t12 = cn("absolute z-10 flex flex-col gap-1.5", t11, className);
    $[11] = className;
    $[12] = t11;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] !== handleZoomIn || $[15] !== handleZoomOut || $[16] !== showZoom) {
    t13 = showZoom && /*#__PURE__*/_jsxs(ControlGroup, {
      children: [/*#__PURE__*/_jsx(ControlButton, {
        onClick: handleZoomIn,
        label: "Zoom in",
        children: /*#__PURE__*/_jsx(Plus, {
          className: "size-4"
        })
      }), /*#__PURE__*/_jsx(ControlButton, {
        onClick: handleZoomOut,
        label: "Zoom out",
        children: /*#__PURE__*/_jsx(Minus, {
          className: "size-4"
        })
      })]
    });
    $[14] = handleZoomIn;
    $[15] = handleZoomOut;
    $[16] = showZoom;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== handleResetBearing || $[19] !== showCompass) {
    t14 = showCompass && /*#__PURE__*/_jsx(ControlGroup, {
      children: /*#__PURE__*/_jsx(CompassButton, {
        onClick: handleResetBearing
      })
    });
    $[18] = handleResetBearing;
    $[19] = showCompass;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== handleLocate || $[22] !== showLocate || $[23] !== waitingForLocation) {
    t15 = showLocate && /*#__PURE__*/_jsx(ControlGroup, {
      children: /*#__PURE__*/_jsx(ControlButton, {
        onClick: handleLocate,
        label: "Find my location",
        disabled: waitingForLocation,
        children: waitingForLocation ? /*#__PURE__*/_jsx(Loader2, {
          className: "size-4 animate-spin"
        }) : /*#__PURE__*/_jsx(Locate, {
          className: "size-4"
        })
      })
    });
    $[21] = handleLocate;
    $[22] = showLocate;
    $[23] = waitingForLocation;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== handleFullscreen || $[26] !== showFullscreen) {
    t16 = showFullscreen && /*#__PURE__*/_jsx(ControlGroup, {
      children: /*#__PURE__*/_jsx(ControlButton, {
        onClick: handleFullscreen,
        label: "Toggle fullscreen",
        children: /*#__PURE__*/_jsx(Maximize, {
          className: "size-4"
        })
      })
    });
    $[25] = handleFullscreen;
    $[26] = showFullscreen;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t12 || $[29] !== t13 || $[30] !== t14 || $[31] !== t15 || $[32] !== t16) {
    t17 = /*#__PURE__*/_jsxs("div", {
      className: t12,
      children: [t13, t14, t15, t16]
    });
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
    $[31] = t15;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  return t17;
}
function CompassButton(t0) {
  const $ = _c(6);
  const {
    onClick
  } = t0;
  const {
    map
  } = useMap();
  const compassRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== map) {
    t1 = () => {
      if (!map || !compassRef.current) {
        return;
      }
      const compass = compassRef.current;
      const updateRotation = () => {
        const bearing = map.getBearing();
        const pitch = map.getPitch();
        compass.style.transform = `rotateX(${pitch}deg) rotateZ(${-bearing}deg)`;
      };
      map.on("rotate", updateRotation);
      map.on("pitch", updateRotation);
      updateRotation();
      return () => {
        map.off("rotate", updateRotation);
        map.off("pitch", updateRotation);
      };
    };
    t2 = [map];
    $[0] = map;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = /*#__PURE__*/_jsxs("svg", {
      ref: compassRef,
      viewBox: "0 0 24 24",
      className: "size-5 transition-transform duration-200",
      style: {
        transformStyle: "preserve-3d"
      },
      children: [/*#__PURE__*/_jsx("path", {
        d: "M12 2L16 12H12V2Z",
        className: "fill-red-500"
      }), /*#__PURE__*/_jsx("path", {
        d: "M12 2L8 12H12V2Z",
        className: "fill-red-300"
      }), /*#__PURE__*/_jsx("path", {
        d: "M12 22L16 12H12V22Z",
        className: "fill-muted-foreground/60"
      }), /*#__PURE__*/_jsx("path", {
        d: "M12 22L8 12H12V22Z",
        className: "fill-muted-foreground/30"
      })]
    });
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== onClick) {
    t4 = /*#__PURE__*/_jsx(ControlButton, {
      onClick: onClick,
      label: "Reset bearing to north",
      children: t3
    });
    $[4] = onClick;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
export { MapControls };